import * as React from 'react'

import { TargetBalance } from 'modules/timeoff/lib/enums'

import type { ImportedEmployee } from './ImportedEmployee'

interface SetEmployeesProps {
  employees: ImportedEmployee[]
  attributes?: string[]
  customFields?: string[]
  isOverlimited?: boolean
  currentSeats?: number
  newEmployees?: number
  maxEmployeesAllowedImport?: number
  templateId?: string
}

type State = {
  files: File[]
  employees: ImportedEmployee[]
  token: string | null | undefined
  readyEmployees: ImportedEmployee[]
  attributes: string[]
  customFields: string[]
  hasEmployees: boolean
  hasFile: boolean
  currentSeats: number
  newEmployees: number
  isOverlimited: boolean
  maxEmployeesAllowedImport: number
  targetBalance: TargetBalance
  skipNotifications: boolean
  templateId?: string
}

export interface EmployeeImporterContextProps extends State {
  setFiles: (files: File[]) => void
  setEmployees: (arg0: SetEmployeesProps) => void
  setToken: (token: string) => void
  setTargetBalance: (targetBalance: TargetBalance) => void
  setSkipNotifications: (skipNotifications: boolean) => void
}

const EmployeeImporterProviderContext = React.createContext<EmployeeImporterContextProps | null>(
  null
)

interface SetFilesAction {
  type: 'set_files'
  payload: {
    files: File[]
  }
}

interface SetEmployeesAction {
  type: 'set_employees'
  payload: {
    employees: ImportedEmployee[]
    attributes: string[]
    customFields: string[]
    isOverlimited: boolean
    currentSeats: number
    newEmployees: number
    maxEmployeesAllowedImport: number
    templateId: string | undefined
  }
}

interface SetTokenAction {
  type: 'set_token'
  payload: {
    token: string
  }
}

interface SetTargetBalanceAction {
  type: 'set_target_balance'
  payload: {
    targetBalance: TargetBalance
  }
}

interface SetSkipNotificationsAction {
  type: 'set_skip_notifications'
  payload: {
    skipNotifications: boolean
  }
}

type Action =
  | SetFilesAction
  | SetEmployeesAction
  | SetTokenAction
  | SetTargetBalanceAction
  | SetSkipNotificationsAction

const reducer = (state: State, action: Action) => {
  const { type, payload } = action

  switch (type) {
    case 'set_files': {
      const files = payload.files

      return { ...state, files, hasFile: files.length > 0 }
    }

    case 'set_employees': {
      const employees = payload.employees

      return {
        ...state,
        employees,
        attributes: payload.attributes,
        customFields: payload.customFields,
        hasEmployees: employees.length > 0,
        currentSeats: payload.currentSeats,
        newEmployees: payload.newEmployees,
        isOverlimited: payload.isOverlimited,
        maxEmployeesAllowedImport: payload.maxEmployeesAllowedImport,
        templateId: payload.templateId,
        readyEmployees: employees.filter((employee) => employee.status === 'ready'),
      }
    }

    case 'set_token': {
      return { ...state, token: payload.token }
    }

    case 'set_target_balance': {
      return { ...state, targetBalance: payload.targetBalance }
    }

    case 'set_skip_notifications': {
      return { ...state, skipNotifications: payload.skipNotifications }
    }

    default:
      throw new Error(`Type of action: ${type} not found on employeesImporterContext.js`)
  }
}

export const EmployeesImporterProvider = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = React.useReducer(reducer, {
    files: [],
    employees: [],
    attributes: [],
    customFields: [],
    token: null,
    targetBalance: TargetBalance.Available,
    readyEmployees: [],
    hasEmployees: false,
    hasFile: false,
    currentSeats: 0,
    newEmployees: 0,
    isOverlimited: false,
    maxEmployeesAllowedImport: 0,
    templateId: undefined,
    skipNotifications: false,
  })

  const setFiles = (files: File[]) => {
    dispatch({
      type: 'set_files',
      payload: {
        files,
      },
    })
  }

  const setEmployees = ({
    employees = [],
    attributes = [],
    customFields = [],
    isOverlimited = false,
    currentSeats = 0,
    newEmployees = 0,
    maxEmployeesAllowedImport = 0,
    templateId = undefined,
  }: SetEmployeesProps) => {
    dispatch({
      type: 'set_employees',
      payload: {
        employees,
        attributes,
        customFields,
        isOverlimited,
        currentSeats,
        newEmployees,
        maxEmployeesAllowedImport,
        templateId,
      },
    })
  }

  const setToken = (token: string) => {
    dispatch({
      type: 'set_token',
      payload: {
        token,
      },
    })
  }

  const setTargetBalance = (targetBalance: TargetBalance) => {
    dispatch({
      type: 'set_target_balance',
      payload: {
        targetBalance,
      },
    })
  }

  const setSkipNotifications = (skipNotifications: boolean) => {
    dispatch({
      type: 'set_skip_notifications',
      payload: {
        skipNotifications,
      },
    })
  }

  return (
    <EmployeeImporterProviderContext.Provider
      value={{ ...state, setFiles, setEmployees, setToken, setTargetBalance, setSkipNotifications }}
    >
      {children}
    </EmployeeImporterProviderContext.Provider>
  )
}

export const useEmployeesImporter = () => {
  const storeContext = React.useContext(EmployeeImporterProviderContext)

  if (!storeContext) {
    throw new Error('useEmployeesImporter must be used within a EmployeesImporterProvider')
  }

  return storeContext
}
